import querystring from 'query-string';
import DataStore from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';
import { CommandResult } from 'src/core/stores/types';
import { Languages } from 'src/i18n';

export enum EventType {
  ScheduledUpdate = 'Update',
  Announcement = 'Announcement',
}

export type AnnouncementLocalizedData = {
  language: Languages;
  text?: string;
  image?: string;
};

type AnnouncementLocalizedDataQueryItem = {
  language: Languages;
  text?: string;
  bannerImage?: number[];
};

export interface ScheduledEventSummary {
  id: string;
  type: EventType;
  createdOn: Date;
  announcementStartDate: Date;
  announcementEndDate: Date;
  scheduledUpdateStart?: Date;
  announcementDuration?: number;
}

export interface ScheduledEventItem extends ScheduledEventSummary {
  announcementLink?: string;
  announcementInfo: AnnouncementLocalizedData[];
}

export interface ScheduledEventQueryItem extends ScheduledEventSummary {
  announcementLink?: string;
  announcementInfo: AnnouncementLocalizedDataQueryItem[];
}

function base64ToBytes(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.toArray() as number[];
}

function dataUrlToBytes(dataUrl: string) {
  const base64 = dataUrl.split(',')[1];
  return base64ToBytes(base64);
}

export class ScheduledEventSummaryStore extends DataStore<ScheduledEventSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/scheduled-announcement`, []);
  }

  public async deleteItems(items: string[]) {
    return await this.handleCallAsync(async () => {
      return await this.httpService.delete<{ announcements: string[] }, CommandResult<{ announcements: string[] }>>(`${this.baseUrl}`, {
        announcements: items,
      });
    });
  }
}

export class ScheduledEventItemStore extends FormStore<ScheduledEventItem, ScheduledEventItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/scheduled-announcement`);
  }

  public async load(id: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<ScheduledEventQueryItem>(`${this.baseUrl}/${encodeURIComponent(id)}`);
      const result: ScheduledEventItem = {
        ...response.data,
        announcementInfo: response.data.announcementInfo.map((v) => ({
          language: v.language,
          text: v.text,
          image: v.bannerImage ? 'data:image/jpeg;base64,' + v.bannerImage : undefined,
        })),
      };
      this._state.set((s) => {
        s.item = result;
        return s;
      });
      return true;
    });
  }

  public async create(item: ScheduledEventItem) {
    return await this.handleCallAsync(async () => {
      const postItem: ScheduledEventQueryItem = {
        ...item,
        announcementInfo: (item?.announcementInfo ?? []).map((v) => ({
          language: v?.language,
          text: v?.text,
          bannerImage: v?.image ? dataUrlToBytes(v.image) : undefined,
        })),
      };
      var result = await this.httpService.post<ScheduledEventQueryItem, CommandResult<ScheduledEventQueryItem>>(`${this.baseUrl}`, postItem);
      return result.data;
    });
  }

  public async save(id: string, item: ScheduledEventItem) {
    return await this.handleCallAsync(async () => {
      const postItem: ScheduledEventQueryItem = {
        ...item,
        id,
        announcementInfo: (item?.announcementInfo ?? []).map((v) => ({
          language: v?.language,
          text: v?.text,
          bannerImage: v?.image ? dataUrlToBytes(v.image) : undefined,
        })),
      };

      var result = await this.httpService.put<ScheduledEventQueryItem, CommandResult<ScheduledEventItem>>(`${this.baseUrl}`, postItem);
      return result.data;
    });
  }

  public async loadWithSelectedLanguages(id: string, language: Languages) {
    return await this.handleCallAsync(async () => {
      const params = querystring.stringify({
        language,
      });
      var response = await this.httpService.get<ScheduledEventQueryItem>(`${this.baseUrl}/${encodeURIComponent(id)}?${params}`);
      const result: ScheduledEventItem = {
        ...response.data,
        announcementInfo: response.data.announcementInfo.map((v) => ({
          language: v.language,
          text: v.text,
          image: v.bannerImage ? 'data:image/jpeg;base64,' + v.bannerImage : undefined,
        })),
      };
      this._state.set((s) => {
        s.item = result;
        return s;
      });
      return true;
    });
  }
}
